import {
  Button,
  Flex,
  IconButton,
  Td,
  Text,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import {
  AccessGrantDto,
  GrantRole,
  OrganizationRoleType,
  useDeleteOrgUserMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";
import { ConfirmationModalConfig } from "../../../../common/useConfirmationModal";
import { PersonAccessModal } from "./PersonAccessModal";
import { User } from "./types";
import { groupByRole } from "../../../../common/organization/useDepartmentRoles";
import { asGrantRole } from "./roleVocabulary";
import { useGrantRoleName } from "./useGrantRoleName";

interface AccessLine {
  key: string;
  role: string;
  where: string | null;
}

const accessLines = (
  departmentGrants: AccessGrantDto[],
  organizationRoles: OrganizationRoleType[],
  showDepartments: boolean,
  roleName: (role: GrantRole) => string,
  t: TFunction,
): AccessLine[] => [
  ...organizationRoles.map((role) => ({
    key: `org-${role}`,
    role: roleName(asGrantRole(role) as GrantRole),
    where: showDepartments ? t("the whole organization") : null,
  })),
  ...groupByRole(departmentGrants).map((group) => ({
    key: `dep-${group.role}`,
    role: roleName(group.role),
    where: group.departments
      .map((d) => d.name || t("Unknown department"))
      .join(", "),
  })),
];

interface Props {
  user: User;
  confirmationModal: {
    show: (config: ConfirmationModalConfig) => void;
    modal: ReactElement;
  };
  showDepartments: boolean;
  departmentGrants: AccessGrantDto[];
}

export const UserRow = ({
  user,
  confirmationModal,
  showDepartments,
  departmentGrants,
}: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const [deleteUser] = useDeleteOrgUserMutation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const displayer = useApiError();
  const grantRoleName = useGrantRoleName();
  const [showAccess, setShowAccess] = useState(false);

  const lines = accessLines(
    departmentGrants,
    user.roles,
    showDepartments,
    grantRoleName,
    t,
  );

  const triggerDelete = async () => {
    try {
      const updated = await deleteUser({
        orgId: authState.selectedOrg.id,
        userId: user.id,
      }).unwrap();
      toast({ title: t("Invite deleted!"), status: "success" });
      dispatch(updateSelectedOrg(updated));
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const isOwner = user.roles.includes(OrganizationRoleType.Owner);

  return (
    <>
      <Tr key={user.id} _hover={{ backgroundColor: "smBackgroundSecondary" }}>
        <Td pl="5" borderBottomColor="smBorderSubtle">
          <Text fontSize="sm">{user.name}</Text>
          <Text fontSize="xs" color="smMuted" isTruncated title={user.email}>
            {user.email}
          </Text>
        </Td>
        <Td borderBottomColor="smBorderSubtle">
          {lines.length === 0 && <Text color="smMuted">{t("No access")}</Text>}
          {lines.map((line) => (
            <Text key={line.key} fontSize="sm" lineHeight="1.7">
              <Text as="span" fontWeight="semibold">
                {line.role}
              </Text>
              {line.where && (
                <Text as="span" color="smMuted">
                  {" · "}
                  {line.where}
                </Text>
              )}
            </Text>
          ))}
        </Td>
        <Td pr="5" borderBottomColor="smBorderSubtle" textAlign="right">
          {!isOwner && (
            <Flex columnGap="1" justifyContent="flex-end">
              <Button
                variant="ghost"
                colorScheme="teal"
                size="sm"
                onClick={() => setShowAccess(true)}
              >
                {t("Change access")}
              </Button>
              {user.id !== authState.me.id && (
                <IconButton
                  aria-label={t("Remove person") ?? "Remove"}
                  icon={<FaTrash />}
                  variant="ghost"
                  colorScheme="red"
                  size="sm"
                  onClick={() =>
                    confirmationModal.show({
                      title: t("Remove person?"),
                      description: t(
                        "Are you sure you want to remove this person?",
                      ),
                      onConfirmed: async () => {
                        await triggerDelete();
                      },
                    })
                  }
                />
              )}
            </Flex>
          )}
        </Td>
      </Tr>
      {showAccess && (
        <PersonAccessModal
          personId={user.id}
          personName={user.name}
          organizationRoles={user.roles}
          isOpen={showAccess}
          onClose={() => setShowAccess(false)}
        />
      )}
    </>
  );
};
